import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getUniversalCookie, removeUniversalCookie } from '@/cookies';
import { useIsLoggedIn } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { billingKeys } from '@/utils/queryKeys';

import { validateCoupon } from './validateCoupon';

import type { ValidateCouponResponse } from './types';

export const useValidateCoupon = () => {
  const { settings } = useSettings();
  const userIsLoggedIn = useIsLoggedIn();
  const couponCookie = useMemo(() => getUniversalCookie('coupon'), []);

  const { data, isFetched } = useQuery<
    ValidateCouponResponse & { alreadyApplied?: boolean }
  >({
    queryKey: billingKeys.validateCoupon(
      String(couponCookie ?? ''),
      userIsLoggedIn,
      String(settings?.user.id ?? '')
    ),
    queryFn: async () => {
      const response = await validateCoupon();

      if (!response.isValid) {
        removeUniversalCookie('coupon');

        return response;
      }

      if (settings?.subscriptionData?.couponApplied === response.couponId) {
        removeUniversalCookie('coupon');

        return {
          ...response,
          alreadyApplied: true,
        };
      }

      return response;
    },
    enabled: !!couponCookie,
    initialData: {
      couponId: null,
      isValid: false,
      alreadyApplied: undefined,
    },
  });

  // initialData means isPending is never true; wait for the first real check
  return { ...data, isValidating: !!couponCookie && !isFetched };
};
